import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import FunnelLayout from "@/v2/components/FunnelLayout";
import GlassCard from "@/v2/components/GlassCard";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment, hasPaymentsConfigured } from "@/lib/stripe";
import { toast } from "@/hooks/use-toast";

const SUPPORT_EMAIL = "federico@progettostartup.com";

const PRODUCT_LABELS: Record<string, string> = {
  sprint_self_service: "Sprint Self-Service",
  sprint_with_call: "Sprint + Call",
  extension_7_days: "Estensione 7 giorni",
};

const STATUS_LABELS: Record<string, string> = {
  paid: "Pagato",
  pending: "In attesa",
  failed: "Non riuscito",
  refunded: "Rimborsato",
  chargeback: "Contestato",
  cancelled: "Annullato",
};

interface PaymentRow {
  id: string;
  productType: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
  receiptUrl: string | null;
}

export default function Account() {
  const { user, loading: authLoading } = useAuth();
  const [payments, setPayments] = useState<PaymentRow[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const env = hasPaymentsConfigured() ? getStripeEnvironment() : "sandbox";
        const { data, error } = await supabase.functions.invoke(
          `v2-billing-history?environment=${env}`,
          { method: "GET" },
        );
        if (cancelled) return;
        if (error) throw error;
        setPayments(data?.payments ?? []);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setLoadError(true);
          setPayments([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  async function handlePasswordSave(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 6) {
      toast({
        title: "Password troppo corta",
        description: "Usa almeno 6 caratteri.",
        variant: "destructive",
      });
      return;
    }
    if (password !== confirm) {
      toast({
        title: "Le password non coincidono",
        description: "Ricontrolla i due campi.",
        variant: "destructive",
      });
      return;
    }
    setSavingPassword(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSavingPassword(false);
    if (error) {
      toast({
        title: "Errore",
        description: error.message || "Non siamo riusciti ad aggiornare la password.",
        variant: "destructive",
      });
      return;
    }
    setPassword("");
    setConfirm("");
    toast({ title: "Password aggiornata", description: "La prossima volta usa la nuova password." });
  }

  if (!authLoading && !user) {
    return <Navigate to="/" replace />;
  }

  return (
    <FunnelLayout>
      <Helmet>
        <title>Il mio account | Proof | Progetto Startup</title>
        <meta name="description" content="Gestisci il tuo account Proof: visualizza ricevute, cambia password e richiedi assistenza." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://progettostartup.com/v2/account" />
      </Helmet>

      <div className="max-w-3xl mx-auto space-y-6">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-secondary">Account</p>
          <h1 className="text-3xl md:text-4xl font-bold">Il mio account</h1>
          <p className="text-sm text-slate-400">{user?.email}</p>
        </div>

        <GlassCard className="space-y-4">
          <h2 className="text-lg font-semibold">Acquisti e ricevute</h2>
          {payments === null ? (
            <p className="text-sm text-slate-400">Caricamento…</p>
          ) : payments.length === 0 ? (
            <p className="text-sm text-slate-400">
              {loadError
                ? "Non siamo riusciti a caricare lo storico. Riprova più tardi."
                : "Non hai ancora effettuato acquisti."}
            </p>
          ) : (
            <ul className="divide-y divide-white/5">
              {payments.map((p) => (
                <li key={p.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-100">
                      {PRODUCT_LABELS[p.productType] ?? p.productType}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(p.createdAt).toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                      {" · "}
                      {STATUS_LABELS[p.status] ?? p.status}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-300">
                      {p.amount.toFixed(2)} {p.currency}
                    </span>
                    {p.receiptUrl && (
                      <a
                        href={p.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-secondary hover:underline"
                      >
                        Ricevuta <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </GlassCard>

        <GlassCard className="space-y-4">
          <h2 className="text-lg font-semibold">Cambia password</h2>
          <form onSubmit={handlePasswordSave} className="space-y-3 max-w-sm">
            <div className="space-y-1">
              <label htmlFor="new-password" className="text-xs text-slate-400">
                Nuova password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
                placeholder="Almeno 6 caratteri"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="confirm-password" className="text-xs text-slate-400">
                Conferma password
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
                placeholder="Ripeti la password"
              />
            </div>
            <Button type="submit" disabled={savingPassword} className="bg-primary hover:bg-primary/90">
              {savingPassword ? "Salvataggio…" : "Aggiorna password"}
            </Button>
          </form>
        </GlassCard>

        <GlassCard className="space-y-2">
          <h2 className="text-lg font-semibold">Rimborsi e cancellazione account</h2>
          <p className="text-sm text-slate-300">
            Per richiedere un rimborso entro 48 ore dall'acquisto scrivi a{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-secondary hover:underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
          <p className="text-sm text-slate-300">
            Per cancellare il tuo account scrivi a{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-secondary hover:underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </GlassCard>
      </div>
    </FunnelLayout>
  );
}
